import React, { useEffect, useState, useCallback } from "react";
import OrdersTable from "./OrdersTable";
import userServices from "services/userServices";
import DeleteModal from "../../../modals/DeleteModal";
import { useNotification } from "providers/NotificationProvider";
import { Container, Row } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { useTranslation } from "context/TranslationProvider";
import UserProfileDetailsModal from "../../../modals/UserProfileDetailsModal";

const AllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const { notify } = useNotification();
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const [tableState, setTableState] = useState({
    page: 1,
    limit: 20,
    sortBy: "orderid",
    sortOrder: -1,
    search: "",
    filters: {
      status: "All",
      dateRange: "All",
    },
  });

  const { page, limit, sortBy, sortOrder, search, filters } = tableState;

  const [openUserModal, setOpenUserModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  const triggerRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    setLoading(true);
    userServices
      .fetchOrders({
        page,
        limit,
        sortBy,
        sortOrder,
        search,
        filters,
      })
      .then((res) => {
        setOrders(res?.data || []);
        setPagination(res?.pagination || {});
      })
      .catch(() => notify("error", "Failed to load orders"))
      .finally(() => setLoading(false));
  }, [page, limit, sortBy, sortOrder, search, filters, notify, refreshKey]);

  const handleTableChange = (type, payload) => {
    if (type === "sort") {
      const { sortField, sortOrder: direction } = payload;
      const newOrder = direction === "asc" ? 1 : -1;

      setTableState((prev) => {
        if (prev.sortBy === sortField && prev.sortOrder === newOrder) return prev;
        return {
          ...prev,
          sortBy: sortField,
          sortOrder: newOrder,
          page: 1,
        };
      });
    }

    if (type === "refresh") {
      triggerRefresh();
    }
  };

  const handleOpenUserDetails = async (orderId, type) => {
    try {
      const res = await userServices.fetchOrderDetails(orderId);
      const orderDetails = res?.data || res;
      let userObj = null;
      if (type === "sender") {
        userObj = orderDetails?.sender_listing_id?.userId;
      } else if (type === "traveler") {
        userObj = orderDetails?.traveler_listing_id?.userId;
      }

      const targetUserId = userObj?._id || (typeof userObj === "string" ? userObj : null);
      if (targetUserId) {
        try {
          const fullUserRes = await userServices.getUserDetails(targetUserId);
          const fullUser = fullUserRes?.data || fullUserRes;
          setSelectedUserDetails(fullUser || userObj);
        } catch {
          setSelectedUserDetails(userObj);
        }
        setOpenUserModal(true);
      } else {
        notify("error", t("page.orders.noOrderDetails", "No order details found."));
      }
    } catch (err) {
      notify("error", "Failed to load user details");
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setItemToDelete(null);
  };

  const deleteRow = (item) => {
    setItemToDelete(item);
    setOpenModal(true);
  };

  const deleteOrder = () => {
    if (!itemToDelete?._id) return;

    setStatusLoadingId(itemToDelete._id);
    userServices
      .deleteOrder(itemToDelete._id)
      .then((res) => {
        if (res?.success === true) {
          notify("success", t(res?.messageKey || "page.orders.toast.orderDeleted", res?.message || t("page.orders.toast.orderDeleted", "Order deleted successfully.")));
          
          setOrders(prev => prev.map(o => 
            o._id === itemToDelete._id ? { ...o, deleted: true, status: 'Cancelled' } : o
          ));

          triggerRefresh();
        } else {
          const errMsgKey = res?.messageKey || res?.error || "toast.unexpectedError";
          notify("error", t(errMsgKey, res?.message || t("toast.unexpectedError", "Unexpected error occurred.")));
        }
      })
      .catch((err) => {
        const errPayload = err?.response?.data || {};
        const errMsgKey = errPayload?.messageKey || errPayload?.error || "toast.unexpectedError";
        notify("error", t(errMsgKey, errPayload?.message || t("toast.unexpectedError", "Unexpected error occurred.")));
      })
      .finally(() => {
        setStatusLoadingId(null);
        handleModalClose();
      });
  };

  return (
    <div className="card-header-full-height">
      <SimpleHeader name={t("sideNav.orders", "Orders")} parentName={t("sideNav.dashboard", "Dashboard")} />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div>
               <OrdersTable
                orders={orders}
                setOrders={setOrders}
                pagination={pagination}
                tableState={tableState}
                setTableState={setTableState}
                onTableChange={handleTableChange}
                deleteRow={deleteRow}
                handleOpenUserDetails={handleOpenUserDetails}
                isItemsLoading={loading}
                statusLoadingId={statusLoadingId}
              />
            </div>
          </div>
        </Row>
      </Container>

       <DeleteModal
        isOpen={openModal}
        toggle={handleModalClose}
        handleClose={handleModalClose}
        onDelete={deleteOrder}
        itemName="order"
        loading={statusLoadingId !== null}
      />

      <UserProfileDetailsModal
        isOpen={openUserModal}
        toggle={() => setOpenUserModal(false)}
        user={selectedUserDetails}
      />

      <UserProfileDetailsModal
        isOpen={openUserModal}
        toggle={() => {
          setOpenUserModal(false);
          setSelectedUserDetails(null);
        }}
        user={selectedUserDetails}
      />
    </div>
  );
};

export default AllOrders;